import Keycloak from "keycloak-js";
import Component from "../../../Component";
/**
 * @namespace sap.mcsuite.model.ts.handlers
 */
export default class AuthentificationHandler {
	private keycloak: Keycloak;
	private component: Component;

	constructor(component: Component) {
		this.component = component;
		const keycloakConfig = {
			url: this.component.getKeycloakURL(),
			realm: this.component.getRealm(),
			clientId: this.component.getClientId(),
		};

		const keycloakInstance: unknown = new Keycloak(keycloakConfig);

		// Validate the instance
		if (!(keycloakInstance instanceof Keycloak)) {
			throw new Error("Keycloak instance is not correctly initialized.");
		}

		// Assign the validated instance
		this.keycloak = keycloakInstance as Keycloak;

		// Safely access onAuthSuccess
		this.keycloak.onAuthSuccess = this.initializeUserSessionAndMenu.bind(this);
	}
	/**
	 * Initialize Keycloak
	 */
	public initialize(): void {
		try {
			this.keycloak.init({
				onLoad: "login-required",
				responseMode: "query",
			});
		} catch (error) {
			console.error("Failed to initialize Keycloak:", error);
		}
	}

	/**
	 * Initialize user session and menu after successful Keycloak authentication
	 */
	public initializeUserSessionAndMenu(): void {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-call
		this.keycloak
			.loadUserProfile()
			.then(
				 (profile: {
					username: string;
					email: string;
					firstName: string;
					lastName: string;
					attributes?:{ internalID?: string[]; };
				}) => {
					const internalId =
						parseInt(profile.attributes?.internalID[0]) || 0;
					this.component
						.getUser()
						.setUsername(profile.username)
						.setEmail(profile.email)
						.setFirstName(profile.firstName)
						.setLastName(profile.lastName)
						.setLocale(this.keycloak.idTokenParsed?.locale || "en")
						.setToken(this.keycloak.token)
						.setRefreshToken(this.keycloak.refreshToken)
						.setIdToken(this.keycloak.idToken)
						.setId(internalId);
					this.component.setTokenInODataRequestHeaders();
					this.component.getUser().authenticate();
					this.component.getUser().requestScopes(this.component.getODataModel()).then(
						async () => {
							// TODO: Do it in Module to show message
							const isActive = await this.component.getUser().requestIsActive(this.component.getODataModel());
							if(!isActive) {
								console.warn("User is not active");
								return; 
							}
							await this.component.getMenu().initialize(this.component.getUser());
							this.component.getRouter().initialize();
							this.component.getRouter().getHashChanger().setHash(window.location.hash.replace('#/', ''));
							this.removeAuthOverlay();
						}
					).catch((error) => {
						console.error("Failed to request roles:", error);
					});
					this.component.fillTranslationCaches();
				}
			)
			.catch((error: Error) => {
				console.error("Failed to load user profile:", error);
			});

		// Start the token refresh loop.
		setTimeout(this.refreshToken.bind(this), 60 * 1000);
	}

	/**
	 * Refresh the Keycloak token
	 */
	private refreshToken(): void {
		// check if the token is still valid for 30 seconds
		this.keycloak
			.updateToken(30)
			.then((refreshed: boolean) => {
				if (refreshed) {
					// set new token
					this.component
						.getUser()
						.setToken(this.keycloak.token)
						.setRefreshToken(this.keycloak.refreshToken)
						.setIdToken(this.keycloak.idToken);
					// set locale
					const idTokenParsed = this.keycloak.idTokenParsed as
						| { locale?: string }
						| undefined;
					const locale = idTokenParsed?.locale || "en-US";
					this.component.getUser().setLocale(locale);
					console.log("Token refreshed successfully:", refreshed);
				}
			})
			.catch((error: Error) => {
				console.error("Failed to refresh token:", error);
			})
			.finally(() => {
				setTimeout(this.refreshToken.bind(this), 60 * 1000);
			});
	}

	private removeAuthOverlay(): void {
		const overlay = document.getElementById("authOverlay");
		if (overlay) {
			overlay.style.opacity = "0";
			setTimeout(() => overlay.remove(), 200);
		}
	}

	public logout(): void {
		this.keycloak.logout();
	}
	
	public getKeycloak(): Keycloak {
		return this.keycloak;
	}
}
